// 使用统计页面 - Tailwind CSS 现代简约风格
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { BarChart3, Activity, DollarSign, Clock, TrendingUp, Zap, Calendar, Filter, RefreshCw } from 'lucide-react';
import { api } from '../lib/api';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import LoadingSpinner from '../components/LoadingSpinner';

interface UsageRecord {
  id: string;
  model: string;
  status: string;                    // API调用状态
  final_cost: number;
  timestamp: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  duration_seconds?: number;
  metadata?: any;
  // 新增：异步任务相关字段
  is_async?: boolean;               // 是否为异步任务
  task_id?: string;                // 异步任务ID
  task_status?: {                  // 异步任务状态
    id: string;
    status: string;                // pending/running/completed/failed
    created_at: string;
    completed_at?: string;
    error_message?: string;
  } | null;
  display_status?: string;         // 前端应该显示的状态
}

interface Statistics {
  total_calls: number;
  total_spent: number;
  successful_calls: number;
  failed_calls: number;
  average_cost: number;
}

const Usage: React.FC = () => {
  const [usageRecords, setUsageRecords] = useState<UsageRecord[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false); // 新增：列表局部加载状态
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    fetchUsageData(1, pageSize, true); // 初始加载
  }, [pageSize]);

  // 组件挂载时获取统计数据
  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchUsageData = async (page = 1, size = pageSize, isInitialLoad = false) => {
    // 初始加载时设置全局loading，其他时候只设置列表loading
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setListLoading(true);
    }
    
    try {
      const response = await api.get(`/api/user/usage?page=${page}&limit=${size}`);

      const { data, pagination } = response.data;

      // 更新分页数据
      setUsageRecords(data || []);
      setTotalRecords(pagination?.total_records || 0);
      setTotalPages(pagination?.total_pages || 0);
      setCurrentPage(pagination?.current_page || page);

      // 记录最后更新时间
      setLastFetchTime(Date.now());

    } catch (err: any) {
      
      setError(err.response?.data?.error?.message || 'Failed to load usage data');
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setListLoading(false);
      }
      setIsRefreshing(false); 
    }
  };

  // 获取统计数据的独立函数
const fetchStatistics = async () => {
  try {
    const response = await api.get('/api/user/info');
    const userStats = response.data.statistics;

    if (userStats) {
      setStatistics({
        total_calls: userStats.total_calls || 0,
        successful_calls: userStats.successful_calls || 0,
        failed_calls: userStats.failed_calls || 0,
        total_spent: userStats.total_spent || 0,
        average_cost: userStats.average_cost || 0
      });
    }
  } catch (err: any) {
    
  }
};

  // 处理页码变化（只获取分页数据，不重新获取统计）
  const handlePageChange = (page: number) => {
    fetchUsageData(page, pageSize);
  };

  // 处理每页条数变化（只获取分页数据，不重新获取统计）
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
    fetchUsageData(1, newPageSize);
  };

  // 手动刷新统计数据的函数
  const refreshStatistics = () => {
    fetchStatistics();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatRelativeTime = useCallback((dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}, []);

  // 获取状态颜色（增强版 - 支持异步任务状态）
  const getStatusColor = (status: string, isAsync: boolean = false) => {
    switch (status) {
      // 同步调用状态
      case 'success': return 'text-green-600 bg-green-100';
      case 'failed': return 'text-red-600 bg-red-100';
      
      // 异步任务状态
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'running': return 'text-blue-600 bg-blue-100';
      case 'completed': return 'text-green-600 bg-green-100';
      
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // 获取状态显示文本
  const getStatusText = (status: string, isAsync: boolean = false) => {
    switch (status) {
      // 同步调用状态
      case 'success': return isAsync ? 'Charged' : 'Success';
      case 'failed': return 'Failed';
      
      // 异步任务状态  
      case 'pending': return 'Waiting';
      case 'running': return 'Processing';
      case 'completed': return 'Completed';
      
      default: return status;
    }
  };

  // 检查异步任务是否需要刷新状态
  const needsRefresh = (record: UsageRecord) => {
    return record.is_async && 
           record.task_status && 
           ['pending', 'running'].includes(record.task_status.status);
  };

  const renderUsageInfo = (record: UsageRecord) => {
    // 从metadata中获取计费信息，如果没有则根据token判断
    const metadata = record.metadata as any;
    const pricingType = metadata?.pricing_type ||
      (record.total_tokens > 0 ? 'token' : 'fixed');

    switch (pricingType) {
      case 'token':
        return (
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">In:</span>
              <span className="text-gray-700 font-medium">{record.input_tokens?.toLocaleString() || 0}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Out:</span>
              <span className="text-gray-700 font-medium">{record.output_tokens?.toLocaleString() || 0}</span>
            </div>
            <div className="flex justify-between text-xs font-medium border-t border-gray-200 pt-1">
              <span className="text-gray-700">Total:</span>
              <span className="text-gray-900">{record.total_tokens?.toLocaleString() || 0}</span>
            </div>
          </div>
        );

      case 'fixed':
        return (
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Type:</span>
              <span className="text-gray-700 font-medium">Fixed Price</span>
            </div>
            <div className="flex justify-between text-xs font-medium border-t border-gray-200 pt-1">
              <span className="text-gray-700">Cost:</span>
              <span className="text-gray-900">${record.final_cost?.toFixed(4) || '0.0000'}</span>
            </div>
          </div>
        );

      case 'duration':
        const duration = metadata?.duration_seconds || record.duration_seconds || 0;
        return (
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Type:</span>
              <span className="text-gray-700 font-medium">Duration</span>
            </div>
            <div className="flex justify-between text-xs font-medium border-t border-gray-200 pt-1">
              <span className="text-gray-700">Time:</span>
              <span className="text-gray-900">{duration.toFixed(1)}s</span>
            </div>
          </div>
        );

      case 'hybrid':
        return (
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Tokens:</span>
              <span className="text-gray-700 font-medium">{record.total_tokens?.toLocaleString() || 0}</span>
            </div>
            <div className="flex justify-between text-xs font-medium border-t border-gray-200 pt-1">
              <span className="text-gray-700">Cost:</span>
              <span className="text-gray-900">${record.final_cost?.toFixed(4) || '0.0000'}</span>
            </div>
          </div>
        );

      default:
        return (
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Type:</span>
              <span className="text-gray-700 font-medium">Custom</span>
            </div>
            <div className="flex justify-between text-xs font-medium border-t border-gray-200 pt-1">
              <span className="text-gray-700">Cost:</span>
              <span className="text-gray-900">${record.final_cost?.toFixed(4) || '0.0000'}</span>
            </div>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <LoadingSpinner size="medium" text="Loading usage statistics..." fullPage={true} />
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl mb-6 shadow-sm">
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* 左右布局 */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
            {/* 左侧：统计卡片 */}
            <div className="xl:col-span-1">
              {statistics && (
                <div className="xl:sticky xl:top-24 space-y-6">
                  <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 hover:shadow-xl transition-all duration-300">
                    <div className="text-center">
                      <p className="text-xs font-medium text-slate-600 mb-1">Total API Calls</p>
                      <p className="text-2xl font-bold text-slate-900">{statistics.total_calls.toLocaleString()}</p>
                      <p className="text-xs text-slate-500 mt-2">
                        <span className="text-green-600 font-medium">{statistics.successful_calls} successful</span>
                        <span className="mx-1">•</span>
                        <span className="text-red-500 font-medium">{statistics.failed_calls} failed</span>
                      </p>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 hover:shadow-xl transition-all duration-300">
                    <div className="text-center">
                      <p className="text-xs font-medium text-slate-600 mb-1">Total Spent</p>
                      <p className="text-2xl font-bold text-slate-900">
                        ${statistics.total_spent.toFixed(4).replace(/\.?0+$/, '')}
                      </p>
                      <p className="text-xs text-slate-500 mt-2">All time usage costs</p>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 hover:shadow-xl transition-all duration-300">
                    <div className="text-center">
                      <p className="text-xs font-medium text-slate-600 mb-1">Success Rate</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {statistics.total_calls > 0
                          ? Math.round((statistics.successful_calls / statistics.total_calls) * 100)
                          : 0
                        }%
                      </p>
                      <p className="text-xs text-slate-500 mt-2">API call reliability</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 右侧：API调用历史 */}
            <div className="xl:col-span-3">
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden relative">
            <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-bold text-slate-900">Recent API Calls</h3>
                  <button
                    onClick={() => {
                      setIsRefreshing(true);
                      fetchUsageData(currentPage, pageSize, false); // 非初始加载
                      refreshStatistics(); // 同时刷新统计数据
                    }}
                    disabled={isRefreshing || listLoading}
                    className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-white rounded-lg transition-all duration-200 disabled:opacity-50"
                    title="Refresh data"
                  >
                    <RefreshCw className={`h-4 w-4 ${isRefreshing || listLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>
                <div className="text-sm text-slate-500">
                  Latest {usageRecords.length} calls
                  {lastFetchTime > 0 && (
                    <span className="ml-2 text-xs">
                      • Updated {Math.floor((Date.now() - lastFetchTime) / 1000)}s ago

                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* 列表加载状态覆盖层 */}
             {listLoading && (
               <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                 <LoadingSpinner size="small" text="Updating..." />
               </div>
             )}
            
            {usageRecords.length > 0 ? (
              <div className={`divide-y divide-slate-200 ${listLoading ? 'opacity-50' : ''}`}>
                {usageRecords.map((record) => {
                  const formattedTime = formatRelativeTime(record.timestamp);

                  return (
                    <div key={record.id} className="p-4 hover:bg-slate-50 transition-colors duration-200">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-slate-900">{record.model}</h4>
                          <p className="text-xs text-slate-500">{formattedTime}</p>
                        </div>

                        <div className="flex items-center space-x-6 text-xs">
                          <div className="text-right">
                            {renderUsageInfo(record)}
                          </div>

                          
                            <div className="text-right">
                              <p className="text-sm font-bold text-slate-900">${record.final_cost.toFixed(4)}</p>
                              <div className="flex items-center gap-1 mt-1">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  getStatusColor(record.display_status || record.status, record.is_async)
                                }`}>
                                  {getStatusText(record.display_status || record.status, record.is_async)}
                                </span>
                                
                                {/* 异步任务特殊标识 */}
                                {record.is_async && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                                    Async
                                  </span>
                                )}
                                
                                {/* 进行中的任务显示刷新按钮 */}
                                {needsRefresh(record) && (
                                  <button
                                    onClick={() => fetchUsageData(currentPage, pageSize)}
                                    className="ml-1 p-1 text-blue-500 hover:text-blue-700 rounded"
                                    title="Refresh task status"
                                  >
                                    <RefreshCw className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                              
                              {/* 异步任务详情链接 */}
                              {record.is_async && record.task_id && (
                                <button
                                  onClick={() => window.open(`/v1/tasks/${record.task_id}`, '_blank')}
                                  className="text-xs text-blue-600 hover:text-blue-800 mt-1"
                                >
                                  View Details
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    
                  );
                })}

                {/* 分页控件 */}
                {totalRecords > 0 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    loading={listLoading}
                  />
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <h3 className="text-base font-semibold text-slate-900 mb-2">No Usage Data</h3>
                <p className="text-sm text-slate-600 text-center max-w-md">
                  Start making API calls to see your usage statistics here
                </p>
              </div>
            )}
          </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Usage;